import React, { useState } from 'react';
import { facultyService } from '../api/facultyService';
import { X, Save, Loader2, Sparkles, Plus, Check, AlertTriangle, Activity } from 'lucide-react';
import Select from 'react-select';
import { regionOptions, getCountryOptions, countryData } from '../utils/countryData';

const FacultyForm = ({ initialData, onSuccess, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [isVerified, setIsVerified] = useState(!!initialData);
    const [rateLimits, setRateLimits] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [formData, setFormData] = useState({
        facultyId: initialData?.FacultyID || null,
        name: initialData?.Name || '',
        organisation: initialData?.Organisation || '',
        designation: initialData?.Designation || '',
        areaOfExpertise: initialData?.AreaOfExpertise || '',
        region: initialData?.Region || '',
        country: initialData?.Country || '',
        about: initialData?.About || '',
        linkedInUrl: initialData?.LinkedInURL || '',
        skills: initialData?.Skills || [],
        createdAt: initialData?.CreatedAt || null
    });
    const [newSkill, setNewSkill] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'about' && isVerified && !initialData) {
            setIsVerified(false);
        }
        setErrorMsg(null);
    };

    const handleRegionChange = (option) => {
        const value = option ? option.value : '';
        setFormData(prev => ({ ...prev, region: value }));
        // Optionally clear country if region changes and country doesn't belong
        if (value && formData.country) {
            const countryMeta = countryData.find(c => c.country === formData.country);
            if (countryMeta && countryMeta.region !== value) {
                setFormData(prev => ({ ...prev, country: '' }));
            }
        }
    };

    const handleCountryChange = (option) => {
        const value = option ? option.value : '';
        setFormData(prev => {
            const updates = { country: value };
            if (value) {
                // Auto-fill region if not already matching
                const countryMeta = countryData.find(c => c.country === value);
                if (countryMeta) updates.region = countryMeta.region;
            }
            return { ...prev, ...updates };
        });
    };

    const handleExtractSkills = async () => {
        if (!formData.about) return;
        setLoading(true);
        setErrorMsg(null);
        try {
            const result = await facultyService.extractSkills(formData.about);

            if (result.rate_limits) {
                setRateLimits(result.rate_limits);
            }

            setFormData(prev => ({
                ...prev,
                skills: result.skills || []
            }));
            setIsVerified(true);
        } catch (error) {
            console.error('Error extracting skills:', error);
            const errBody = error.response || error;
            if (errBody.rate_limits) {
                setRateLimits(errBody.rate_limits);
            }

            const message = errBody.error || 'Failed to analyze bio. You can still add skills manually.';
            setErrorMsg(message);
            setIsVerified(true); // Allow manual entry even on error
        } finally {
            setLoading(false);
        }
    };

    const handleAddSkill = (e) => {
        e.preventDefault();
        if (!newSkill.trim()) return;
        if (!formData.skills.includes(newSkill.trim())) {
            setFormData(prev => ({
                ...prev,
                skills: [...prev.skills, newSkill.trim()]
            }));
        }
        setNewSkill('');
    };

    const removeSkill = (skillToRemove) => {
        setFormData(prev => ({
            ...prev,
            skills: prev.skills.filter(s => s !== skillToRemove)
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await facultyService.saveFaculty(formData);
            onSuccess();
        } catch (error) {
            console.error('Error saving faculty:', error);
            alert('Failed to save faculty details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={isVerified ? handleSave : (e) => e.preventDefault()}
            style={{
                display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '900px', width: '100%',
                maxHeight: '90vh',
                background: 'linear-gradient(155deg, #dbeafe 0%, #ede9fe 42%, #ccfbf1 100%)',
                padding: '2.5rem', borderRadius: '16px', border: '1px solid #c7d2fe',
                boxShadow: '0 12px 35px rgba(30, 41, 59, 0.1)',
                color: '#1e293b',
                position: 'relative', overflow: 'hidden'
            }}
        >
            {/* Header */}
            <div style={{ paddingBottom: '1rem', borderBottom: '1px solid rgba(0,0,0,0.05)', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h3 style={{ margin: '0 0 0.25rem 0', color: '#1e3a8a', fontSize: '1.4rem', fontWeight: 700 }}>
                        {initialData ? 'Update Expert Profile' : 'Register External Expert'}
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>Provide professional details for AI-powered skill extraction and verification.</p>
                </div>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{
                        background: 'rgba(255, 255, 255, 0.5)', border: 'none', borderRadius: '50%',
                        padding: '0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.2s', color: '#64748b'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)'}
                >
                    <X size={20} />
                </button>
            </div>

            {/* AI Rate Limit Monitoring - Floating Indicator */}
            {rateLimits && (
                <div style={{
                    position: 'absolute', top: '7rem', right: '2.5rem',
                    background: 'rgba(255, 255, 255, 0.8)', padding: '0.6rem 1rem', borderRadius: '12px',
                    border: '1px solid #d1fae5', boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                    display: 'flex', flexDirection: 'column', gap: '0.3rem', pointerEvents: 'none',
                    zIndex: 10
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', fontWeight: 700, color: '#059669' }}>
                        <Activity size={14} />
                        AI QUOTA STATUS
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                            Reqs: <span style={{ color: '#1e293b', fontWeight: 600 }}>{rateLimits.remaining_requests || '?'}/{rateLimits.limit_requests || '?'}</span>
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                            Tokens: <span style={{ color: '#1e293b', fontWeight: 600 }}>{Math.round((rateLimits.remaining_tokens || 0) / 1000)}k/{Math.round((rateLimits.limit_tokens || 0) / 1000)}k</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Scrollable Content Container */}
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '0.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Grid Content */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '1.5rem' }}>
                    <div style={{ gridColumn: 'span 6' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                            Full Name
                        </label>
                        <input
                            required
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Dr. John Doe"
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', outline: 'none', background: 'white' }}
                        />
                    </div>

                    <div style={{ gridColumn: 'span 6' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                            Designation
                        </label>
                        <input
                            required
                            name="designation"
                            value={formData.designation}
                            onChange={handleChange}
                            placeholder="Senior Research Fellow"
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', outline: 'none', background: 'white' }}
                        />
                    </div>

                    <div style={{ gridColumn: 'span 6' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                            Organisation
                        </label>
                        <input
                            required
                            name="organisation"
                            value={formData.organisation}
                            onChange={handleChange}
                            placeholder="Institute of Economic Studies"
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', outline: 'none', background: 'white' }}
                        />
                    </div>

                    <div style={{ gridColumn: 'span 6' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                            Area of Expertise
                        </label>
                        <input
                            required
                            name="areaOfExpertise"
                            value={formData.areaOfExpertise}
                            onChange={handleChange}
                            placeholder="Macroeconomics, Trade Policy"
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', outline: 'none', background: 'white' }}
                        />
                    </div>

                    <div style={{ gridColumn: 'span 6' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                            Region (optional)
                        </label>
                        <Select
                            options={regionOptions}
                            value={regionOptions.find(opt => opt.value === formData.region) || null}
                            onChange={handleRegionChange}
                            isClearable
                            placeholder="Select Region"
                            styles={{ control: (base) => ({ ...base, padding: '0.1rem', borderRadius: '8px', borderColor: '#cbd5e1' }) }}
                        />
                    </div>

                    <div style={{ gridColumn: 'span 6' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                            Country (optional)
                        </label>
                        <Select
                            options={getCountryOptions(formData.region)}
                            value={getCountryOptions(formData.region).find(opt => opt.value === formData.country) || null}
                            onChange={handleCountryChange}
                            isClearable
                            placeholder="Select Country"
                            styles={{ control: (base) => ({ ...base, padding: '0.1rem', borderRadius: '8px', borderColor: '#cbd5e1' }) }}
                        />
                    </div>

                    <div style={{ gridColumn: 'span 12' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                            LinkedIn Profile URL (optional)
                        </label>
                        <input
                            name="linkedInUrl"
                            value={formData.linkedInUrl}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/in/username"
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', outline: 'none', background: 'white' }}
                        />
                    </div>

                    <div style={{ gridColumn: 'span 12' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                                Detailed About / Bio
                            </label>
                            {!isVerified && (
                                <div style={{
                                    fontSize: '0.75rem', color: '#2563eb', background: '#eff6ff',
                                    padding: '0.2rem 0.6rem', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '0.3rem',
                                    border: '1px solid #dbeafe'
                                }}>
                                    <Sparkles size={12} />
                                    AI extraction available after input
                                </div>
                            )}
                        </div>
                        <textarea
                            required
                            name="about"
                            value={formData.about}
                            onChange={handleChange}
                            rows={isVerified ? 3 : 6}
                            placeholder="Enter full bio or background details here. Our AI will automatically extract skill tags from this text..."
                            style={{ width: '100%', padding: '1rem', borderRadius: '12px', border: '1px solid #cbd5e1', outline: 'none', background: 'white', fontSize: '0.95rem', lineHeight: 1.6, resize: 'none', transition: 'all 0.3s' }}
                        />
                    </div>

                    {/* Error Message for Rate Limits */}
                    {errorMsg && (
                        <div style={{
                            gridColumn: 'span 12', background: '#fef2f2', border: '1px solid #fecaca',
                            padding: '1rem', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '0.75rem',
                            color: '#991b1b', fontSize: '0.9rem', fontWeight: 600
                        }}>
                            <AlertTriangle size={20} />
                            <div>
                                {errorMsg}
                                <div style={{ fontSize: '0.8rem', fontWeight: 400, marginTop: '0.25rem', opacity: 0.8 }}>
                                    You can still proceed by adding tags manually in the section below.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Skill Verification Section */}
                    {isVerified && (
                        <div style={{ gridColumn: 'span 12', background: 'rgba(255, 255, 255, 0.4)', padding: '1.5rem', borderRadius: '14px', border: '1px dashed #cbd5e1', marginTop: '0.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Check size={18} className="text-green-600" />
                                    Review & Refine Expert Skills
                                </h4>
                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{formData.skills.length} tags identified</span>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1.25rem' }}>
                                {formData.skills.map((skill, index) => (
                                    <span
                                        key={index}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                                            padding: '0.4rem 0.8rem', borderRadius: '10px', background: 'white',
                                            fontSize: '0.85rem', fontWeight: 600, color: '#334155', border: '1px solid #e2e8f0',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                        }}
                                    >
                                        {skill}
                                        <button
                                            type="button"
                                            onClick={() => removeSkill(skill)}
                                            style={{ border: 'none', background: 'transparent', padding: 0.1, cursor: 'pointer', color: '#94a3b8' }}
                                        >
                                            <X size={14} />
                                        </button>
                                    </span>
                                ))}
                                {formData.skills.length === 0 && (
                                    <p style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>No skills found. Add some below.</p>
                                )}
                            </div>

                            {/* Add Custom Skill */}
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input
                                    value={newSkill}
                                    onChange={(e) => setNewSkill(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddSkill(e)}
                                    placeholder="Add a custom skill tag..."
                                    style={{ flex: 1, padding: '0.6rem 0.9rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem', outline: 'none' }}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddSkill}
                                    style={{
                                        padding: '0.6rem 1.25rem', background: 'white', color: '#2563eb',
                                        border: '1px solid #2563eb', borderRadius: '8px', cursor: 'pointer',
                                        fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem'
                                    }}
                                >
                                    <Plus size={16} />
                                    Add Tag
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{
                        padding: '0.75rem 2rem', background: 'white', color: '#475569', fontWeight: 600,
                        border: '1px solid #cbd5e1', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                    Cancel
                </button>

                {!isVerified ? (
                    <button
                        type="button"
                        onClick={handleExtractSkills}
                        disabled={loading || !formData.about}
                        style={{
                            padding: '0.75rem 2.5rem',
                            background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                            color: 'white', fontWeight: 700,
                            border: 'none', borderRadius: '12px', cursor: 'pointer',
                            opacity: (loading || !formData.about) ? 0.6 : 1,
                            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                            transition: 'all 0.2s',
                            display: 'flex', alignItems: 'center', gap: '0.5rem'
                        }}
                    >
                        {loading ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Extracting Skills...
                            </>
                        ) : (
                            <>
                                <Sparkles size={18} />
                                Analyze Bio & Extract Skills
                            </>
                        )}
                    </button>
                ) : (
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '0.75rem 2.5rem',
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                            color: 'white', fontWeight: 700,
                            border: 'none', borderRadius: '12px', cursor: 'pointer',
                            opacity: loading ? 0.7 : 1,
                            boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
                            transition: 'all 0.2s',
                            display: 'flex', alignItems: 'center', gap: '0.5rem'
                        }}
                    >
                        {loading ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Saving Profile...
                            </>
                        ) : (
                            <>
                                <Save size={18} />
                                {initialData ? 'Confirm & Update Profile' : 'Confirm & Register Expert'}
                            </>
                        )}
                    </button>
                )}
            </div>
        </form>
    );
};

export default FacultyForm;
